'use client';

import {cva, type VariantProps} from 'class-variance-authority';

import {cn} from '../../lib/utils';

import type * as React from 'react';

const spinnerVariantsConfig = {
  variants: {
    size: {
      sm: 'size-3',
      md: 'size-4',
      lg: 'size-5',
      xl: 'size-7',
      '2xl': 'size-16'
    },
    tone: {
      current: '',
      brand: 'text-brand-primary'
    }
  },
  defaultVariants: {
    size: 'md',
    tone: 'current'
  }
} as const;

const spinnerVariants = cva('shrink-0 animate-spin', spinnerVariantsConfig);

type SpinnerProps = Omit<React.ComponentProps<'svg'>, 'color'> &
  VariantProps<typeof spinnerVariants>;

/** Decorative unless given an `aria-label` — pair it with visible loading text, or label it when it stands alone. */
function Spinner({className, size, tone, 'aria-label': ariaLabel, ...props}: SpinnerProps) {
  return (
    <svg
      data-slot="spinner"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      className={cn(spinnerVariants({size, tone}), className)}
      role={ariaLabel ? 'status' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      {...props}
    >
      <circle cx="12" cy="12" r="10" strokeOpacity={0.15} />
      <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
    </svg>
  );
}

export {Spinner};
